
import { stableRecordKey } from '../lib/rowIdentity';
import React from 'react';
import { Calendar, MapPin, Users, Ticket, Shield, Minus, Plus, Bell, CheckCircle, XCircle, Lock, IdCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { EventShareButtons } from '../components/promo/EventPromo';
export const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
export const isStudentCat = c => c?.is_student || /student/i.test(c?.id || '') || /student/i.test(c?.name || '');
export const requiresMemberCat = c => c?.requires_member || c?.id === 'member';
export const EventCategoryOption = ({
  c,
  label,
  locked,
  noSeason
}) => <option value={c.id} disabled={locked || noSeason}>
                                                {label}{locked ? ' (members only)' : ''}{noSeason ? ' (day ticket only)' : ''}
                                              </option>;
export const LegacyTicketSelection = ({
  quantities,
  setQuantities,
  ticketTypes
}) => <div className="space-y-3" data-testid="ticket-selection">
                            <Label className="text-sm font-medium">Select tickets</Label>
                            {Object.entries(ticketTypes).map(([type, info]) => <div key={type} className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--gs-saffron))] transition-colors">
                                <div>
                                  <div className="text-sm font-medium capitalize">{info.label || type.replace('_', ' ')}</div>
                                  <div className="text-lg font-semibold text-[hsl(var(--gs-saffron))] tabular-nums" style={{
        fontFamily: 'Space Grotesk'
      }}>£{info.price}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button onClick={() => setQuantities(q => ({
        ...q,
        [type]: Math.max(0, (q[type] || 0) - 1)
      }))} disabled={!quantities[type]} className="w-8 h-8 rounded-lg border border-[hsl(var(--border))] flex items-center justify-center hover:bg-[hsl(var(--accent))] disabled:opacity-30" data-testid={`ticket-minus-${type}`}><Minus className="w-3 h-3" /></button>
                                  <span className="w-6 text-center font-medium tabular-nums" data-testid={`ticket-qty-${type}`}>{quantities[type] || 0}</span>
                                  <button onClick={() => setQuantities(q => ({
        ...q,
        [type]: (q[type] || 0) + 1
      }))} className="w-8 h-8 rounded-lg border border-[hsl(var(--border))] flex items-center justify-center hover:bg-[hsl(var(--accent))]" data-testid={`ticket-plus-${type}`}><Plus className="w-3 h-3" /></button>
                                </div>
                              </div>)}
                          </div>;
export const EventInformation = ({
  event,
  eventDate,
  eventDays,
  handleReminder,
  isMultiDay,
  isPast,
  remaining,
  reminderPhone,
  reminderSubmitted,
  setReminderPhone
}) => <div className="lg:col-span-2">
              {event.flyer_preview_url ? <a href={`${BACKEND}${event.flyer_url}`} target="_blank" rel="noreferrer" className="block mb-6 group" title="Open flyer" data-testid="event-flyer-link">
                  <img src={`${BACKEND}${event.flyer_animated ? event.flyer_url : event.flyer_preview_url}`} alt={`${event.title} flyer`} className="w-full max-h-[32rem] object-contain rounded-2xl border bg-white shadow-sm group-hover:shadow-md transition-shadow" data-testid="event-flyer-image" />
                  <span className="block text-center text-xs text-[hsl(var(--muted-foreground))] mt-1">Tap the flyer to open {event.flyer_kind === 'pdf' ? 'the PDF' : 'full size'}</span>
                </a> : event.image_url && <img src={event.image_url} alt={event.title} className="w-full h-64 sm:h-80 object-cover rounded-2xl mb-6" />}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge className="bg-[hsl(var(--gs-saffron-soft))] text-[hsl(var(--gs-ink))]">
                  <Calendar className="w-3 h-3 mr-1" />
                  {eventDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}
                </Badge>
                {isMultiDay && <Badge variant="outline">{eventDays.length}-day event</Badge>}
                {eventDate.getHours() > 0 && <Badge variant="outline">{eventDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })}</Badge>}
                {isPast && <Badge variant="destructive">Past Event</Badge>}
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3" style={{
    fontFamily: 'Space Grotesk'
  }} data-testid="event-detail-title">{event.title}</h1>

              {/* Share buttons (links carry the flyer as a picture preview on WhatsApp / Facebook) */}
              <div className="mb-4"><EventShareButtons eventId={event.event_id} compact /></div>

              {(event.venue || event.location) && <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-4">
                  <MapPin className="w-4 h-4" />
                  <span>{event.venue || event.location}</span>
                  <a href={event.venue_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue || event.location)}`} target="_blank" rel="noreferrer" className="text-[hsl(var(--ring))] text-sm hover:underline" data-testid="event-maps-link">
                    View on Google Maps →
                  </a>
                </div>}
              {event.show_capacity && <>
                  <div className="flex items-center gap-4 text-sm text-[hsl(var(--muted-foreground))] mb-6" data-testid="event-capacity-info">
                    <div className="flex items-center gap-1"><Users className="w-4 h-4" /> Capacity: {event.capacity}</div>
                    <div className="flex items-center gap-1"><Ticket className="w-4 h-4" /> Remaining: {remaining}</div>
                  </div>
                  <Separator className="mb-6" />
                </>}
              <div className="prose prose-sm max-w-none">
                <p className="text-[hsl(var(--muted-foreground))] leading-relaxed whitespace-pre-wrap">{event.description}</p>
              </div>

              {/* Event Reminder */}
              <Card className="mt-8" data-testid="event-reminder-section">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2" style={{
        fontFamily: 'Space Grotesk'
      }}><Bell className="w-5 h-5 text-[hsl(var(--gs-saffron))]" /> Get event reminder</CardTitle></CardHeader>
                <CardContent>
                  {reminderSubmitted ? <div className="flex items-center gap-2 text-[hsl(var(--gs-success))]">
                      <CheckCircle className="w-5 h-5" />
                      <span>Thank you - we will send you a WhatsApp reminder before the event.</span>
                    </div> : <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input placeholder="WhatsApp number (with country code, e.g., +44...)" value={reminderPhone} onChange={e => setReminderPhone(e.target.value)} data-testid="event-reminder-phone-input" className="flex-1" />
                        <Button onClick={handleReminder} variant="outline" data-testid="event-reminder-submit-button">Set Reminder</Button>
                      </div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        We'll send you a WhatsApp reminder 24 hours before the event
                      </p>
                    </div>}
                </CardContent>
              </Card>
            </div>;
export const EventAttendeeRow = ({
  cat,
  formatDay,
  groupSeason,
  i,
  isFixedDayCat,
  isMultiDay,
  lastDay,
  memberForm,
  memberVerified,
  offeredCategories,
  setTickets,
  student,
  t,
  ticketCost,
  tickets,
  updateTicket
}) => <div className="p-3 rounded-lg border border-[hsl(var(--border))] space-y-2.5" data-testid={`ticket-card-${i}`}>
                                      <div className="flex items-center gap-2">
                                        <select value={t.category_id} onChange={e => updateTicket(i, {
      category_id: e.target.value
    })} className="flex-1 rounded-lg border border-[hsl(var(--input))] px-2.5 py-2 text-sm bg-transparent" data-testid={`ticket-category-${i}`}>
                                          {offeredCategories.map(c => {
        const locked = requiresMemberCat(c) && !memberVerified;
        const fixed = isFixedDayCat(c);
        const noSeason = false;
        const label = fixed ? `${c.name} — £${c.price} (${formatDay(c.fixed_day)} only)` : groupSeason ? `${c.name} — £${c.season_price} season` : `${c.name} — £${c.price}${isMultiDay ? '/day' : ''}`;
        return <EventCategoryOption key={c.id} c={c} label={label} locked={locked} noSeason={noSeason} />;
      })}
                                        </select>
                                        <button onClick={() => setTickets(tickets.filter((_, idx) => idx !== i))} className="text-[hsl(var(--destructive))] p-1" data-testid={`ticket-remove-${i}`}>
                                          <Minus className="w-4 h-4" />
                                        </button>
                                      </div>
                                      <div className="flex gap-2">
                                        <Input placeholder={`Person ${i + 1} full name`} value={t.name} onChange={e => updateTicket(i, {
      name: e.target.value
    })} className="flex-1" data-testid={`ticket-name-${i}`} />
                                        <select value={t.gender} onChange={e => updateTicket(i, {
      gender: e.target.value
    })} className="w-28 rounded-lg border border-[hsl(var(--input))] px-2 py-2 text-sm bg-transparent" data-testid={`ticket-gender-${i}`}>
                                          <option value="">Gender</option>
                                          <option value="male">Male</option>
                                          <option value="female">Female</option>
                                          <option value="prefer_not_to_say">Prefer not to say</option>
                                        </select>
                                      </div>
                                      {requiresMemberCat(cat) && <div className="space-y-1 p-2 rounded-lg bg-blue-50 border border-blue-200">
                                          <Input placeholder={`${t.name || `Person ${i + 1}`}'s GS membership number`} value={t.membership_number} onChange={e => updateTicket(i, {
      membership_number: e.target.value
    })} className="h-9 text-sm" data-testid={`ticket-member-number-${i}`} />
                                          <p className="text-[11px] text-blue-700" data-testid={`ticket-member-hint-${i}`}>
                                            Member tickets are for the member only. The name above must match the name on this membership — if it does not match, the booking is refused and a non-member ticket should be chosen. Leave blank only if this ticket is for you ({memberForm.membership_number || 'your verified number'}).
                                          </p>
                                        </div>}
                                      {student && <div className="space-y-1.5 p-2 rounded-lg bg-[hsl(var(--muted)/0.4)]">
                                          <div className="flex gap-2">
                                            <Input placeholder="Student ID number (optional)" value={t.student_id} onChange={e => updateTicket(i, {
        student_id: e.target.value
      })} className="flex-1 h-9 text-sm" data-testid={`ticket-student-id-${i}`} />
                                            <Input type="date" title="Student ID expiry date" value={t.student_expiry} onChange={e => updateTicket(i, {
        student_expiry: e.target.value
      })} className="w-36 h-9 text-sm" data-testid={`ticket-student-expiry-${i}`} />
                                          </div>
                                          <p className="text-[11px] text-amber-700 flex items-start gap-1">
                                            <IdCard className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                            {t.student_expiry ? 'Please carry your physical student ID to the event.' : `Your student ID must be valid until the event date${lastDay ? ` (${formatDay(lastDay)})` : ''}. Please carry your physical student ID to the event.`}
                                          </p>
                                        </div>}
                                      <div className="flex items-center justify-between text-sm">
                                        <span className="text-xs text-[hsl(var(--muted-foreground))]">{cat?.name}</span>
                                        <span className="font-semibold text-[hsl(var(--gs-saffron))] tabular-nums" data-testid={`ticket-cost-${i}`}>£{ticketCost(t).toFixed(2)}</span>
                                      </div>
                                    </div>;
export const EventTicketSelection = ({
  addTicket,
  anySeasonCat,
  catById,
  daysChosen,
  eventDays,
  formatDay,
  groupDays,
  groupSeason,
  isFixedDayCat,
  isMultiDay,
  lastDay,
  memberForm,
  memberVerified,
  offeredCategories,
  qrCount,
  setGroupDays,
  setGroupSeason,
  setTickets,
  ticketCost,
  tickets,
  toggleGroupDay,
  updateTicket,
  visibleCategories
}) => { const todayUK = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' }); return <div className="space-y-4" data-testid="ticket-selection">
                              {/* STEP 1: Days / season for the whole group */}
                              {isMultiDay && <div className="space-y-2">
                                  <Label className="text-sm font-medium">1. Which day(s) is your group attending?</Label>
                                  {anySeasonCat && <button onClick={() => {
      setGroupSeason(!groupSeason);
      setGroupDays([]);
    }} className={`w-full p-2.5 rounded-lg border text-sm font-medium transition-colors ${groupSeason ? 'border-[hsl(var(--gs-saffron))] bg-[hsl(var(--gs-saffron-soft))] text-[hsl(var(--gs-ink))]' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--gs-saffron))]'}`} data-testid="group-season-button">
                                      {groupSeason ? '✓ ' : ''}Season Ticket — all {eventDays.length} days
                                    </button>}
                                  {!groupSeason && <div className="flex flex-wrap gap-1.5">
                                      {eventDays.map(day => <button key={day} disabled={day < todayUK} title={day < todayUK ? 'This date is already past' : undefined} onClick={() => day >= todayUK && toggleGroupDay(day)} className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${day < todayUK ? 'opacity-40 cursor-not-allowed line-through bg-[hsl(var(--muted))]' : ''} ${groupDays.includes(day) ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]'}`} data-testid={`group-day-${day}`}>
                                          {formatDay(day)}{day < todayUK ? ' (past)' : ''}
                                        </button>)}
                                    </div>}
                                </div>}

                              {/* STEP 2: Tickets (max 10) */}
                              <div className={`space-y-3 ${isMultiDay && !daysChosen ? 'opacity-40 pointer-events-none' : ''}`}>
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium">{isMultiDay ? '2. ' : ''}Who is coming? ({tickets.length}/10)</Label>
                                  <Button size="sm" variant="outline" onClick={addTicket} disabled={tickets.length >= 10} data-testid="add-ticket-button">
                                    <Plus className="w-3 h-3 mr-1" /> Add ticket
                                  </Button>
                                </div>
                                {tickets.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))]">Add up to 10 tickets — you'll enter a name for each person.</p>}
                                {tickets.map((t, i) => {
      const cat = catById(t.category_id);
      const student = isStudentCat(cat);
      return <EventAttendeeRow key={stableRecordKey(t)} cat={cat} formatDay={formatDay} groupSeason={groupSeason} i={i} isFixedDayCat={isFixedDayCat} isMultiDay={isMultiDay} lastDay={lastDay} memberForm={memberForm} memberVerified={memberVerified} offeredCategories={offeredCategories} setTickets={setTickets} student={student} t={t} ticketCost={ticketCost} tickets={tickets} updateTicket={updateTicket} />;
    })}
                                {!memberVerified && visibleCategories.some(requiresMemberCat) && <p className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1">
                                    <Lock className="w-3 h-3" /> Member tickets unlock after membership verification above.
                                  </p>}
                              </div>

                              {tickets.length > 0 && daysChosen && <div className="p-2.5 rounded-lg bg-[hsl(var(--gs-saffron-soft))] text-xs text-[hsl(var(--gs-ink))]" data-testid="qr-info-note">
                                  Your group will receive <strong>{qrCount} QR code{qrCount !== 1 ? 's' : ''}</strong> — one per person per day, each with their name on it. <strong>1 QR = 1 entry</strong> at the gate.
                                </div>}
                            </div>; };
export const EventPurchaseCard = ({
  familySharedEmail,
  setFamilySharedEmail,
  addTicket,
  anySeasonCat,
  catById,
  checkingOut,
  customerEmail,
  customerName,
  daysChosen,
  eventDays,
  formatDay,
  groupDays,
  groupSeason,
  handleCheckout,
  handleValidateMember,
  hasCategories,
  isFixedDayCat,
  isMember,
  isMultiDay,
  lastDay,
  memberForm,
  memberValid,
  memberVerified,
  offeredCategories,
  qrCount,
  quantities,
  setCustomerEmail,
  setCustomerName,
  setGroupDays,
  setGroupSeason,
  setIsMember,
  setMemberForm,
  setMemberValid,
  setQuantities,
  setTickets,
  ticketCost,
  ticketTypes,
  tickets,
  toggleGroupDay,
  total,
  totalTickets,
  updateTicket,
  validating,
  visibleCategories
}) => <Card className="shadow-lg border-2 border-[hsl(var(--gs-saffron)/0.35)]" data-testid="event-purchase-card">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-2xl sm:text-3xl" style={{
      fontFamily: 'Space Grotesk'
    }}>Purchase tickets</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {/* Member Question */}
                      {isMember === null && <div data-testid="member-question">
                          <Label className="text-sm font-medium mb-2 block">Are you a GS member?</Label>
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Verified members unlock member-priced tickets for their group.</p>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setIsMember('yes')} className="flex-1" data-testid="member-yes-button">Yes, I'm a member</Button>
                            <Button variant="outline" onClick={() => setIsMember('no')} className="flex-1" data-testid="member-no-button">No</Button>
                          </div>
                        </div>}

                      {/* Member Validation Form */}
                      {isMember === 'yes' && memberValid !== true && <div className="space-y-3" data-testid="member-validation-form">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium" htmlFor="booking-gs-number">GS Membership Number</Label>
                            <button onClick={() => {
          setIsMember(null);
          setMemberValid(null);
        }} className="text-xs text-[hsl(var(--muted-foreground))] hover:underline">Cancel</button>
                          </div>
                          <Input id="booking-gs-number" autoComplete="off" placeholder="e.g. GSNG12092605031234" value={memberForm.membership_number} onChange={e => setMemberForm({
        ...memberForm,
        membership_number: e.target.value
      })} data-testid="member-number-input" />
                          <p className="text-sm text-muted-foreground" data-testid="booking-gs-number-helper">Use your activated GS number. No date of birth is needed for ticket booking. Only ticket eligibility is confirmed; no personal details are shown.</p>
                          {memberValid === false && <div className="flex items-center gap-2 text-sm text-[hsl(var(--destructive))]">
                              <XCircle className="w-4 h-4" /> Member not found. Check details or continue as non-member.
                            </div>}
                          <div className="flex gap-2">
                            <Button onClick={handleValidateMember} disabled={validating || !memberForm.membership_number.trim()} className="flex-1 bg-[hsl(var(--primary))] text-white" data-testid="member-validate-button">
                              {validating ? 'Verifying...' : 'Verify'}
                            </Button>
                            <Button variant="ghost" onClick={() => {
          setIsMember('no');
          setMemberValid(null);
        }} className="text-sm" data-testid="continue-non-member-button">
                              Skip
                            </Button>
                          </div>
                        </div>}

                      {/* Member Verified */}
                      {memberVerified && <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200" data-testid="member-verified-badge">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <div className="text-sm font-medium text-green-800">Member verified — member tickets unlocked</div>
                            <div className="text-xs text-green-600">{memberForm.membership_number}</div>
                          </div>
                        </div>}

                      {/* Non-member confirmed */}
                      {isMember === 'no' && <div className="flex items-center justify-between">
                          <span className="text-sm text-[hsl(var(--muted-foreground))]">Purchasing as non-member</span>
                          <button onClick={() => {
        setIsMember(null);
        setMemberValid(null);
      }} className="text-xs text-[hsl(var(--gs-saffron))] hover:underline">Change</button>
                        </div>}

                      {/* Ticket Selection */}
                      {(isMember === 'no' || memberVerified) && <>
                          <Separator />
                          {hasCategories ? <EventTicketSelection addTicket={addTicket} anySeasonCat={anySeasonCat} catById={catById} daysChosen={daysChosen} eventDays={eventDays} formatDay={formatDay} groupDays={groupDays} groupSeason={groupSeason} isFixedDayCat={isFixedDayCat} isMultiDay={isMultiDay} lastDay={lastDay} memberForm={memberForm} memberVerified={memberVerified} offeredCategories={offeredCategories} qrCount={qrCount} setGroupDays={setGroupDays} setGroupSeason={setGroupSeason} setTickets={setTickets} ticketCost={ticketCost} tickets={tickets} toggleGroupDay={toggleGroupDay} updateTicket={updateTicket} visibleCategories={visibleCategories} /> : <LegacyTicketSelection quantities={quantities} setQuantities={setQuantities} ticketTypes={ticketTypes} />}

                          {totalTickets > 0 && (!hasCategories || daysChosen) && <>
                              <Separator />
                              <div className="space-y-3" data-testid="customer-details">
                                <Label className="text-sm font-medium">Your details (booking contact)</Label>
                                <Input placeholder="Full Name" value={customerName} onChange={e => setCustomerName(e.target.value)} data-testid="customer-name-input" />
                                <Input placeholder="Email Address" type="email" value={customerEmail} onChange={e => { setCustomerEmail(e.target.value); setFamilySharedEmail(false); }} data-testid="customer-email-input" />
                              </div>
                              <Separator />
                              <div className="flex items-center justify-between">
                                <span className="font-medium">Total</span>
                                <span className="text-2xl font-bold text-[hsl(var(--gs-saffron))] tabular-nums" style={{
            fontFamily: 'Space Grotesk'
          }} data-testid="checkout-total">£{total.toFixed(2)}</span>
                              </div>
                              <Button onClick={handleCheckout} disabled={checkingOut} className="w-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 h-12 text-base" data-testid="event-detail-buy-tickets-button">
                                {checkingOut ? 'Processing...' : `Pay £${total.toFixed(2)}`}
                              </Button>
                              <div className="flex items-center justify-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                                <Shield className="w-3 h-3" /> Secure payment by Stripe
                              </div>
                            </>}
                        </>}
                    </CardContent>
                  </Card>;
